import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';
import { isAnnouncementDismissed } from './announcementUiStorage';

export type AppAnnouncementKind = 'ops' | 'product';

export type AppAnnouncement = {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'warn';
  /** ops = avisos; product = novedades. Default ops si la columna aún no llegó al cliente. */
  kind: AppAnnouncementKind;
  cta_label: string | null;
  cta_url: string | null;
  sort_order: number;
};

function parseKind(raw: unknown): AppAnnouncementKind {
  return raw === 'product' ? 'product' : 'ops';
}

// Anuncios ops/novedades: contenido editorial que cambia a mano. Cache largo
// sobre las filas crudas; el filtro de dismissals (storage local por user) se
// aplica siempre por llamada.
const announcementsCache = createKeyedTtlCache(5 * 60 * 1000);

async function fetchAnnouncementRowsRemote(): Promise<AppAnnouncement[]> {
  const { data, error } = await supabase
    .from('app_announcements')
    .select('id, title, body, severity, kind, cta_label, cta_url, sort_order')
    .order('sort_order', { ascending: true });
  if (error || !data) return [];

  return (data as Array<Record<string, unknown>>).map((row) => ({
    id: String(row.id),
    title: String(row.title ?? ''),
    body: String(row.body ?? ''),
    severity: row.severity === 'warn' ? ('warn' as const) : ('info' as const),
    kind: parseKind(row.kind),
    cta_label: typeof row.cta_label === 'string' ? row.cta_label : null,
    cta_url: typeof row.cta_url === 'string' ? row.cta_url : null,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
  }));
}

/** Active + date window come from RLS. Fail soft → []. */
export async function fetchVisibleAppAnnouncements(
  userId: string | undefined,
): Promise<AppAnnouncement[]> {
  try {
    const rows = await announcementsCache.get('all', fetchAnnouncementRowsRemote);
    if (!userId) return rows;

    const visible: AppAnnouncement[] = [];
    for (const row of rows) {
      if (!(await isAnnouncementDismissed(userId, row.id))) visible.push(row);
    }
    return visible;
  } catch {
    return [];
  }
}

registerSessionCacheClearHandler(() => announcementsCache.clear());
